import type { KeyboardEvent, MouseEvent } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import { getRegistryEntry, hasRecordPermission, type RecordType } from './registry';
import { useRecordViewer } from './RecordViewer';

/**
 * Props that make a whole table row / card open a record in the viewer (click, Enter, Space).
 * Returns {} (a plain row) when the id is missing, the type is unregistered or the viewer lacks the
 * type's permission. Focus returns to the row's RecordLink, or the row itself, when the viewer closes.
 */
export function useRowOpen() {
  const { open } = useRecordViewer();
  const has = useAuthStore((s) => s.hasPermission);
  return (type: RecordType, id: string | null | undefined, label: string) => {
    const entry = getRegistryEntry(type);
    if (!id || !entry || !hasRecordPermission(entry.permission, has)) return {};
    const go = (el: HTMLElement) =>
      open({ type, id, label }, { returnFocusTo: el.querySelector<HTMLElement>('[data-record-link]') ?? el });
    return {
      role: 'button' as const,
      tabIndex: 0,
      className: 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      onClick: (e: MouseEvent<HTMLElement>) => go(e.currentTarget),
      onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          go(e.currentTarget);
        }
      },
    };
  };
}
